import "./ReservationDocument.scss"
import IconTrashBin from "assets/img/icon-trash-bin.svg?react"

import { FC, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { Draggable, DraggableProvided, DraggableProvidedDragHandleProps, DraggableStateSnapshot } from "@hello-pangea/dnd"
import cn from "classnames"

import { Document } from "models/Document"
import { Grid, GridItem } from "components/Grid"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import Button from "components/Button/Button"
import DragButton from "pages/PrepareShippingPage/DragButton/DragButton"
import Text from "components/Text/Text"
import toastMessages from "stores/toastMessages"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import { IApiLink } from "@/types"


let dragNDropPortal = document.getElementById("root-drag-n-drop-portal")
if (!dragNDropPortal) {
    dragNDropPortal = document.createElement("div")
    dragNDropPortal.setAttribute("id", "root-modal")
    document.body.appendChild(dragNDropPortal)
}

interface IReservationDocumentProps {
    document: Document
    index: number
    disabled?: boolean
    onDelete: (link: IApiLink) => Promise<void>
}

const ReservationDocument: FC<React.PropsWithChildren<IReservationDocumentProps>> = ({
    document,
    index,
    disabled,
    onDelete,
}) => {
    const { t } = useTranslation()
    const { name, extension } = document.getNameAndExtension()
    const isDraggable = !!document.linkUpdateDocument
    const canDrag = isDraggable && !disabled
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    async function handleDelete() {
        if (!document.linkDeleteDocument) return
        setIsDeleting(true)
        try {
            await onDelete(document.linkDeleteDocument)
            setShowDeleteConfirm(false)
            toastMessages.showDataDeleted()
        } catch(e) {
            console.error(e)
            toastMessages.showSystemError()
        } finally {
            setIsDeleting(false)
        }
    }

    const getContent = (dragHandleProps?: DraggableProvidedDragHandleProps | null) => {
        return (
            <>
                <Grid
                    columns={`${isDraggable ? 'max-content ' : ''}minmax(0, auto) max-content 4.8rem`}>
                    {!!isDraggable &&
                        <GridItem className="border-right-grey-100">
                            <DragButton
                                dragHandleProps={dragHandleProps}
                                disabled={!canDrag || !dragHandleProps}
                            />
                        </GridItem>
                    }
                    <GridItem className="pl-1-5 py-2">
                        <a
                            href={document.uri}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="link color-brand-blue"
                            download>
                            <Grid columns="minmax(0, max-content) max-content">
                                <GridItem>
                                    <FlexibleContent text={name} clamp={1} />
                                </GridItem>
                                <GridItem>.{extension}</GridItem>
                            </Grid>
                        </a>
                        {!!document.uploadedByEmail &&
                            <Text type="pSmallest" className="mt-half color-grey-500">
                                <FlexibleContent text={t(document.attachedAutomatically ? 'documents.createdByX' : 'documents.uploadedByX', { user: document.uploadedByEmail })} />
                            </Text>
                        }
                    </GridItem>
                    <GridItem className="px-1-5 py-2">
                        {document.uploadDate.format(t("config.dateLongFormat"))}
                    </GridItem>
                    <GridItem className="py-1 pr-1">
                        {document.linkDeleteDocument &&
                            <Button
                                ariaLabel={t("common.delete")}
                                icon={<IconTrashBin />}
                                theme="transparent"
                                disabled={disabled}
                                onClick={() => setShowDeleteConfirm(true)}
                                round
                            />
                        }
                    </GridItem>
                </Grid>

            </>
        )
    }

    const getDraggableContent = (provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
        <div ref={provided.innerRef}
            {...provided.draggableProps}
            className={cn(
                "reservation-document reservation-document--draggable",
                { "reservation-document--dragging": snapshot.isDragging },
            )}>
            {getContent(provided.dragHandleProps)}
        </div>
    )

    return (
        <>
            {isDraggable
                ? <Draggable draggableId={document.id}
                    isDragDisabled={!canDrag}
                    index={index}
                    disableInteractiveElementBlocking>
                    {(provided, snapshot) => (
                        snapshot.isDragging
                            ? createPortal(getDraggableContent(provided, snapshot), dragNDropPortal!)
                            : getDraggableContent(provided, snapshot)
                    )}
                </Draggable>
                : <div className="reservation-document">
                    {getContent()}
                </div>
            }
            {showDeleteConfirm &&
                <ConfirmModal
                    title={t('common.warning')}
                    maxWidth="586px"
                    theme="danger"
                    confirmButtonLabel={t('common.delete')}
                    isBusy={isDeleting}
                    onClose={() => setShowDeleteConfirm(false)}
                    onConfirm={handleDelete}>
                    {t('documents.deleteConfirm')}
                    <div className="mt-2 font-bold word-break-break-word">
                        {document.name}
                    </div>
                </ConfirmModal>
            }
        </>
    )
}

export default ReservationDocument
